import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, FileText, MoreHorizontal, Trash2 } from "lucide-react"
import { ExportButton } from "@/components/export-button"
import { useInvoices, useDeleteInvoice } from "@/hooks/use-invoices"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useFilterParams } from "@/hooks/use-filter-params"
import { useBulkDelete, useBulkUpdate } from "@/hooks/use-bulk-actions"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Checkbox } from "@/components/ui/checkbox"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { BulkActionBar, buildDeleteAction, buildStatusAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkStatusDialog } from "@/components/bulk-status-dialog"
import { FilterPanel, SelectFilter, DateRangeFilter } from "@/components/filters"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { formatDate, formatCurrency } from "@/lib/utils"
import { toast } from "sonner"
import type { InvoiceStatus, InvoiceType } from "@/lib/types"

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  draft: { label: "Szkic", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  issued: { label: "Wystawiona", color: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  sent: { label: "Wysłana", color: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  paid: { label: "Zapłacona", color: "#10B981", bg: "rgba(16, 185, 129, 0.1)" },
  partially_paid: { label: "Częściowo", color: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)" },
  overdue: { label: "Zaległa", color: "#EF4444", bg: "rgba(239, 68, 68, 0.1)" },
  cancelled: { label: "Anulowana", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  corrected: { label: "Skorygowana", color: "#6366F1", bg: "rgba(99, 102, 241, 0.1)" },
}

const TYPE_LABELS: Record<InvoiceType, string> = {
  vat: "VAT",
  proforma: "Proforma",
  corrective: "Korekta",
  margin: "Marża",
}

const INVOICE_FILTER_KEYS = [
  "status", "invoice_type", "issue_date_from", "issue_date_to", "due_date_from", "due_date_to",
] as const

const INVOICE_COLUMN_IDS = ["number", "invoice_type", "company", "status", "total_net", "total_gross", "amount_paid", "amount_due", "issue_date", "due_date", "owner"]
const DEFAULT_VISIBLE = ["number", "company", "status", "total_net", "total_gross", "amount_paid", "amount_due", "issue_date", "due_date"]

const COLUMN_LABELS: Record<string, string> = {
  number: "Numer",
  invoice_type: "Typ",
  company: "Kontrahent",
  status: "Status",
  total_net: "Netto",
  total_gross: "Brutto",
  amount_paid: "Zaplacono",
  amount_due: "Do zaplaty",
  issue_date: "Data",
  due_date: "Termin",
  owner: "Wlasciciel",
}

const columnOptions: ColumnOption[] = INVOICE_COLUMN_IDS.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}))

export function InvoicesListPage() {
  const navigate = useNavigate()
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(INVOICE_FILTER_KEYS)

  const { data, isLoading } = useInvoices({
    ...filters,
  })
  const deleteMutation = useDeleteInvoice()
  const bulkDeleteMutation = useBulkDelete("invoices")
  const bulkUpdateMutation = useBulkUpdate("invoices")

  const {
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("invoices", INVOICE_COLUMN_IDS, DEFAULT_VISIBLE)

  const invoices = data?.data || []
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length
  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length
  const someSelected = selectedIds.length > 0 && selectedIds.length < invoices.length

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const sel: Record<string, boolean> = {}
      invoices.forEach((inv) => { sel[inv.id] = true })
      setRowSelection(sel)
    } else {
      setRowSelection({})
    }
  }

  const toggleRow = (id: string, checked: boolean) => {
    setRowSelection((prev) => ({ ...prev, [id]: checked }))
  }

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm("Czy na pewno chcesz usunąć tę fakturę?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Faktura usunięta")
  }

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} faktur`)
    setRowSelection({})
  }

  const handleBulkStatus = async (status: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { status },
    })
    toast.success(`Zaktualizowano ${result.updated} faktur`)
    setRowSelection({})
  }

  const bulkActions = [
    buildStatusAction(() => setShowStatusDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="page-title">Faktury</h1>
          <ColumnSelector
            columns={columnOptions}
            visibleCount={visibleCount}
            totalCount={totalCount}
            isColumnVisible={isColumnVisible}
            onToggleColumn={toggleColumn}
            onShowAll={showAll}
            onHideAll={hideAll}
          />
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="invoices"
            filters={filters}
          />
          <Button onClick={() => navigate("/invoices/new")} size="sm">
            <Plus size={16} className="mr-1" /> Nowa faktura
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status || ""}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "draft", label: "Szkic" },
            { value: "issued", label: "Wystawiona" },
            { value: "sent", label: "Wyslana" },
            { value: "paid", label: "Zaplacona" },
            { value: "partially_paid", label: "Czesciowo zaplacona" },
            { value: "overdue", label: "Zalegla" },
          ]}
          placeholder="Wszystkie statusy"
        />
        <SelectFilter
          label="Typ"
          value={filters.invoice_type || ""}
          onChange={(v) => setFilter("invoice_type", v)}
          options={[
            { value: "vat", label: "VAT" },
            { value: "proforma", label: "Proforma" },
            { value: "corrective", label: "Korekta" },
            { value: "margin", label: "Marza" },
          ]}
          placeholder="Wszystkie typy"
        />
        <DateRangeFilter
          label="Data wystawienia"
          fromValue={filters.issue_date_from || ""}
          toValue={filters.issue_date_to || ""}
          onFromChange={(v) => setFilter("issue_date_from", v)}
          onToChange={(v) => setFilter("issue_date_to", v)}
        />
        <DateRangeFilter
          label="Termin platnosci"
          fromValue={filters.due_date_from || ""}
          toValue={filters.due_date_to || ""}
          onFromChange={(v) => setFilter("due_date_from", v)}
          onToChange={(v) => setFilter("due_date_to", v)}
        />
      </FilterPanel>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        onDeselectAll={() => setRowSelection({})}
        actions={bulkActions}
      />

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FileText size={48} className="mb-3 opacity-30" />
          <p className="text-lg font-medium text-foreground">Brak faktur</p>
          <p className="text-sm mt-1">Utwórz pierwszą fakturę, aby rozpocząć</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-4 py-2.5 w-10">
                  <Checkbox
                    checked={allSelected || (someSelected && "indeterminate")}
                    onCheckedChange={(value) => toggleAll(!!value)}
                    aria-label="Zaznacz wszystkie"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                {isColumnVisible("number") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Numer</th>}
                {isColumnVisible("invoice_type") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Typ</th>}
                {isColumnVisible("company") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Kontrahent</th>}
                {isColumnVisible("status") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>}
                {isColumnVisible("total_net") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Netto</th>}
                {isColumnVisible("total_gross") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Brutto</th>}
                {isColumnVisible("amount_paid") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Zaplacono</th>}
                {isColumnVisible("amount_due") && <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Do zaplaty</th>}
                {isColumnVisible("issue_date") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Data</th>}
                {isColumnVisible("due_date") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Termin</th>}
                {isColumnVisible("owner") && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Wlasciciel</th>}
                <th className="px-4 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const statusCfg = STATUS_CONFIG[inv.status]
                return (
                  <tr
                    key={inv.id}
                    className="border-t cursor-pointer transition-colors hover:bg-muted/30"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    data-state={rowSelection[inv.id] ? "selected" : undefined}
                  >
                    <td className="px-4 py-2.5">
                      <Checkbox
                        checked={!!rowSelection[inv.id]}
                        onCheckedChange={(value) => toggleRow(inv.id, !!value)}
                        aria-label="Zaznacz wiersz"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    {isColumnVisible("number") && <td className="px-4 py-2.5 font-medium text-foreground">
                      {inv.number}
                    </td>}
                    {isColumnVisible("invoice_type") && <td className="px-4 py-2.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {TYPE_LABELS[inv.invoice_type]}
                      </span>
                    </td>}
                    {isColumnVisible("company") && <td className="px-4 py-2.5 text-muted-foreground">
                      {inv.company?.name || "\u2014"}
                    </td>}
                    {isColumnVisible("status") && <td className="px-4 py-2.5">
                      <Badge
                        variant="outline"
                        style={{ color: statusCfg.color, backgroundColor: statusCfg.bg, borderColor: statusCfg.color }}
                      >
                        {statusCfg.label}
                      </Badge>
                    </td>}
                    {isColumnVisible("total_net") && <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                      {formatCurrency(inv.total_net)}
                    </td>}
                    {isColumnVisible("total_gross") && <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium text-foreground">
                      {formatCurrency(inv.total_gross)}
                    </td>}
                    {isColumnVisible("amount_paid") && <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${inv.amount_paid > 0 ? "text-green-600" : "text-muted-foreground"}`}>
                      {inv.amount_paid > 0 ? formatCurrency(inv.amount_paid) : "\u2014"}
                    </td>}
                    {isColumnVisible("amount_due") && <td className={`px-4 py-2.5 text-right font-mono tabular-nums font-medium ${inv.amount_due > 0 ? "text-red-500" : "text-green-600"}`}>
                      {formatCurrency(inv.amount_due)}
                    </td>}
                    {isColumnVisible("issue_date") && <td className="px-4 py-2.5 text-muted-foreground">
                      {inv.issue_date ? formatDate(inv.issue_date) : "\u2014"}
                    </td>}
                    {isColumnVisible("due_date") && <td className="px-4 py-2.5 text-muted-foreground">
                      {inv.due_date ? formatDate(inv.due_date) : "\u2014"}
                    </td>}
                    {isColumnVisible("owner") && <td className="px-4 py-2.5 text-muted-foreground">
                      {inv.owner ? `${inv.owner.first_name} ${inv.owner.last_name}` : "\u2014"}
                    </td>}
                    <td className="px-4 py-2.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button className="p-1 rounded hover:bg-muted">
                            <MoreHorizontal size={16} className="text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => handleDelete(inv.id, e as unknown as React.MouseEvent)} className="text-red-600">
                            <Trash2 size={14} className="mr-2" /> Usuń
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="faktur"
        onConfirm={handleBulkDelete}
      />
      <BulkStatusDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        count={selectedCount}
        options={[
          { value: "draft", label: "Szkic" },
          { value: "issued", label: "Wystawiona" },
          { value: "sent", label: "Wyslana" },
          { value: "paid", label: "Zaplacona" },
          { value: "partially_paid", label: "Czesciowo zaplacona" },
          { value: "overdue", label: "Zalegla" },
          { value: "cancelled", label: "Anulowana" },
        ]}
        onConfirm={handleBulkStatus}
      />
    </div>
  )
}
